import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { Eye, EyeOff, ArrowRight, ArrowLeft, Check, Loader2, User, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import awloLogo from '../assets/AWLO1.png';
import authImg from '../assets/authImg.png';
import PhoneInputFull from '../components/ui/PhoneInputFull';

// --- VARIANTES D'ANIMATION ---
const kenBurnsEffect = {
  initial: { scale: 1, x: '0%', y: '0%' },
  animate: {
    scale: 1.1,
    x: '-2%',
    y: '-1%',
    transition: { duration: 20, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }
  }
};

const logoFadeIn = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { delay: 0.5, duration: 1 } }
};

const fadeIn = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 20 } }
};

const formContainerVariants = {
  hidden: { opacity: 0, scale: 0.98 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.4, ease: "easeOut", when: "beforeChildren", staggerChildren: 0.05 }
  }
};

const stepVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 100, damping: 20 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } }
};

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const propertyId = searchParams.get('propertyId');
  const forcedRole = searchParams.get('role');
  const isForcedClient = forcedRole === 'client' && propertyId;

  const [accountType, setAccountType] = useState(isForcedClient ? 'client' : null);
  const [accountTypeSelected, setAccountTypeSelected] = useState(isForcedClient);

  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '', phone: '', role: 'client', professional_info: null
  });
  const [phoneData, setPhoneData] = useState({ formatted: '', e164: '', isValid: false, country: 'FR' });

  const [professionalInfo, setProfessionalInfo] = useState({
    company_name: '', siret: '', professional_type: 'agency'
  });

  const isStep1Valid = () => formData.first_name.trim() !== '' && formData.last_name.trim() !== '' && formData.email.trim() !== '' && formData.password.length >= 6 && phoneData.isValid;
  const isStep2Valid = () => accountType === 'client' ? true : professionalInfo.company_name.trim() !== '';
  const totalSteps = accountType === 'professional' ? 2 : 1;

  const handleAccountTypeSelect = (type) => {
    setAccountType(type);
    setFormData({ ...formData, role: type });
    setAccountTypeSelected(true);
    setCurrentStep(1);
  };

  const handleChangeAccountType = () => {
    setAccountTypeSelected(false);
    setAccountType(null);
    setFormData({ ...formData, first_name: '', last_name: '', email: '', password: '', phone: '', role: 'client' });
    setPhoneData({ formatted: '', e164: '', isValid: false, country: 'FR' });
    setProfessionalInfo({ company_name: '', siret: '', professional_type: 'agency' });
    setCurrentStep(1);
  };

  const handleNextStep = () => { if (currentStep < totalSteps) setCurrentStep(currentStep + 1); };
  const handlePrevStep = () => { if (currentStep > 1) setCurrentStep(currentStep - 1); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accountType === 'professional' && currentStep === 1) {
      handleNextStep();
      return;
    }
    setLoading(true);
    const submitData = {
      email: formData.email,
      password: formData.password,
      firstName: formData.first_name,
      lastName: formData.last_name,
      phone: phoneData.e164 || '',
      role: formData.role
    };

    if (accountType === 'professional') {
      submitData.professionalInfo = {
        companyName: professionalInfo.company_name,
        siret: professionalInfo.siret,
        professionalType: professionalInfo.professional_type
      };
    }

    const result = await register(submitData);
    if (result.success) {
      toast({ title: 'Inscription réussie', description: 'Bienvenue sur ImmoCI !' });
      const userRole = (result.user.role || '').toLowerCase();
      if (userRole === 'professional') navigate('/professional');
      else if (userRole === 'client') {
        if (propertyId) navigate(`/client?addFavorite=${propertyId}`);
        else navigate('/client');
      } else navigate('/');
    } else {
      toast({ title: "Erreur d'inscription", description: result.error, variant: 'destructive' });
    }
    setLoading(false);
  };

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center p-4 sm:p-8 overflow-hidden font-sans">

      {/* --- BACKGROUND --- */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${authImg})` }}
          variants={kenBurnsEffect} initial="initial" animate="animate"
        />
        <div className="absolute inset-0 bg-black/60 backdrop-blur-[2px]" />
      </div>

      {/* Logo flottant au-dessus du background */}
      <motion.div
        variants={logoFadeIn} initial="initial" animate="animate"
        className="absolute top-[30%] left-[20%] md:left-[25%] lg:left-[32%] z-50 hidden md:block pointer-events-auto"
      >
        <Link to="/" className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer">
          <img src={awloLogo} alt="AWLO" className="h-24 lg:h-32 w-auto object-contain drop-shadow-2xl brightness-110" />
        </Link>
      </motion.div>

      {/* --- CONTENT LAYOUT --- */}
      <div className="relative z-10 w-full max-w-6xl grid lg:grid-cols-2 gap-12 items-center">

        {/* --- LEFT: SLOGAN --- */}
        <motion.div
          initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
          className="hidden lg:flex flex-col justify-center items-start space-y-6 pl-10"
        >
          <div className="text-white space-y-2 mt-32">
            <h2 className="text-4xl font-bold leading-tight">Rejoignez<br />l'aventure.</h2>
            <p className="text-white/70 text-lg max-w-md font-light">
              Créez votre compte pour accéder à des opportunités immobilières exclusives.
            </p>
          </div>
        </motion.div>

        {/* --- RIGHT: FORM --- */}
        <div className="flex justify-center lg:justify-end pr-0 lg:pr-10">
          <motion.div
            variants={formContainerVariants} initial="hidden" animate="visible"
            className="w-full max-w-[460px] bg-black/20 backdrop-blur-md rounded-3xl border border-white/10 p-8 shadow-2xl"
          >
            {/* Mobile Logo */}
            <div className="lg:hidden flex justify-center mb-6">
              <Link to="/" className="block hover:opacity-90 transition-opacity focus:outline-none cursor-pointer pointer-events-auto" aria-label="Retour à l'accueil">
                <img src={awloLogo} alt="AWLO" className="h-14 w-auto object-contain drop-shadow-lg" />
              </Link>
            </div>

            <motion.div variants={fadeIn} className="mb-6">
              <h1 className="text-3xl font-bold text-white mb-2 tracking-tight">
                {!accountTypeSelected ? 'Créer un compte' : 'Vos informations'}
              </h1>
            </motion.div>

            <AnimatePresence mode="wait">
              {/* --- STEP 0: CHOIX TYPE COMPTE --- */}
              {!accountTypeSelected && !isForcedClient ? (
                <motion.div key="select-type" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="space-y-4">
                  <p className="text-white/70 text-sm mb-4">Je suis un...</p>
                  <div className="grid grid-cols-2 gap-4">
                    <button onClick={() => handleAccountTypeSelect('client')}
                      className="h-32 bg-white/10 border border-white/20 hover:bg-white/20 hover:border-[#FF9F68] rounded-2xl flex flex-col items-center justify-center gap-3 transition-all group">
                      <div className="p-3 bg-white/10 rounded-full group-hover:bg-[#FF9F68] transition-colors">
                        <User className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-white font-medium">Particulier</span>
                    </button>
                    <button onClick={() => handleAccountTypeSelect('professional')}
                      className="h-32 bg-white/10 border border-white/20 hover:bg-white/20 hover:border-[#FF9F68] rounded-2xl flex flex-col items-center justify-center gap-3 transition-all group">
                      <div className="p-3 bg-white/10 rounded-full group-hover:bg-[#FF9F68] transition-colors">
                        <Building2 className="h-6 w-6 text-white" />
                      </div>
                      <span className="text-white font-medium">Professionnel</span>
                    </button>
                  </div>
                </motion.div>
              ) : (
                /* --- FORMULAIRE INSCRIPTION --- */
                <motion.div key="form-steps" variants={stepVariants} initial="hidden" animate="visible" exit="exit">

                  {/* Changement type compte */}
                  {!isForcedClient && (
                    <div className="mb-6 flex justify-between items-center">
                      <span className="text-xs text-white/50 uppercase tracking-wide">
                        Compte {accountType === 'client' ? 'Particulier' : 'Professionnel'}
                      </span>
                      <button onClick={handleChangeAccountType} className="text-xs text-[#FF9F68] hover:text-white transition-colors underline">
                        Changer
                      </button>
                    </div>
                  )}

                  {/* Indicateur étapes Pro */}
                  {accountType === 'professional' && (
                    <div className="flex items-center gap-2 mb-6">
                      <div className={`h-1.5 flex-1 rounded-full transition-all ${currentStep >= 1 ? 'bg-[#FF9F68]' : 'bg-white/20'}`} />
                      <div className={`h-1.5 flex-1 rounded-full transition-all ${currentStep >= 2 ? 'bg-[#FF9F68]' : 'bg-white/20'}`} />
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <AnimatePresence mode="wait">
                      {currentStep === 1 && (
                        <motion.div key="step1" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                              <Label className="text-xs text-white/80 uppercase ml-1">Prénom</Label>
                              <Input placeholder="Jean" value={formData.first_name} onChange={e => setFormData({ ...formData, first_name: e.target.value })}
                                className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" required />
                            </div>
                            <div className="space-y-1.5">
                              <Label className="text-xs text-white/80 uppercase ml-1">Nom</Label>
                              <Input placeholder="Dupont" value={formData.last_name} onChange={e => setFormData({ ...formData, last_name: e.target.value })}
                                className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" required />
                            </div>
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-white/80 uppercase ml-1">Email</Label>
                            <Input type="email" placeholder="nom@exemple.com" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                              className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" required />
                          </div>
                          <PhoneInputFull
                            defaultCountry="FR"
                            onPhoneChange={(data) => {
                              setPhoneData(data);
                              setFormData((prev) => ({ ...prev, phone: data.formatted }));
                            }}
                          />
                          <div className="space-y-1.5">
                            <Label className="text-xs text-white/80 uppercase ml-1">Mot de passe</Label>
                            <div className="relative">
                              <Input type={showPassword ? 'text' : 'password'} placeholder="6 caractères min." value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })}
                                className="h-11 bg-white/10 border-white/10 rounded-xl text-white pr-10 placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" required minLength={6} />
                              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white">
                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {currentStep === 2 && accountType === 'professional' && (
                        <motion.div key="step2" variants={stepVariants} initial="hidden" animate="visible" exit="exit" className="space-y-4">
                          <div className="space-y-1.5">
                            <Label className="text-xs text-white/80 uppercase ml-1">Entreprise</Label>
                            <Input placeholder="Nom de l'agence" value={professionalInfo.company_name} onChange={e => setProfessionalInfo({ ...professionalInfo, company_name: e.target.value })}
                              className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" required />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-white/80 uppercase ml-1">RCCM (Optionnel)</Label>
                            <Input placeholder="Numéro RCCM" value={professionalInfo.siret} onChange={e => setProfessionalInfo({ ...professionalInfo, siret: e.target.value })}
                              className="h-11 bg-white/10 border-white/10 rounded-xl text-white placeholder:text-white/30 focus:ring-2 focus:ring-[#FF9F68] focus:bg-white/20 transition-all" />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs text-white/80 uppercase ml-1">Type</Label>
                            <Select value={professionalInfo.professional_type} onValueChange={v => setProfessionalInfo({ ...professionalInfo, professional_type: v })}>
                              <SelectTrigger className="h-11 bg-white/10 border-white/10 rounded-xl text-white focus:ring-2 focus:ring-[#FF9F68]">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="agency">Agence immobilière</SelectItem>
                                <SelectItem value="developer">Promoteur</SelectItem>
                                <SelectItem value="individual">Indépendant</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                      {currentStep > 1 && (
                        <Button type="button" onClick={handlePrevStep} variant="outline"
                          className="h-11 px-4 bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white rounded-xl">
                          <ArrowLeft className="h-4 w-4" />
                        </Button>
                      )}
                      <Button type="submit" disabled={loading || (currentStep === 1 && !isStep1Valid()) || (currentStep === 2 && !isStep2Valid())}
                        className="flex-1 h-11 bg-[#FF9F68] hover:bg-[#E67E4F] text-white font-bold rounded-xl shadow-lg shadow-orange-900/20 transition-all disabled:opacity-50">
                        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> :
                          accountType === 'professional' && currentStep === 1 ? <>Suivant <ArrowRight className="h-4 w-4 ml-2" /></> :
                            'Créer mon compte'}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>

            <motion.div variants={fadeIn} className="mt-6 pt-6 border-t border-white/10 text-center">
              <p className="text-sm text-white/60">
                Déjà un compte ?{' '}
                <Link to="/login" className="font-bold text-white hover:text-[#FF9F68] transition-colors">
                  Se connecter
                </Link>
              </p>
            </motion.div>

          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-4 left-0 w-full text-center z-10 pointer-events-none">
        <p className="text-white/20 text-xs font-medium">© 2025 AWLO</p>
      </div>
    </div>
  );
};

export default RegisterPage;